import React, { useEffect, useState } from 'react'
import { faStar as faStarSolid } from '@fortawesome/free-solid-svg-icons'
import { faStar as faStarRegular } from '@fortawesome/free-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useDispatch, useSelector } from 'react-redux'
import { useUpdateCompetencyEvaluationMutation } from '../../redux/growth-wheel.endpoints'
import { ConfirmationModal } from './ConfirmationModal'
import { RootState } from '../../redux/store'
import {
  evaluateFocusCompetenciesLimit,
  setCurrentAreasOfFocus,
} from '../../redux/growthWheel.slice'
import { ConfirmModalType } from '../../utils/growth-wheel-utils'
import { RichTextView } from './RichTextView'
interface SubCompetencyAssessmentTitleCardProps {
  name: string
  description: string
  isFocus?: boolean
  evaluationId?: number
  subjectUserId: number
  subCompetencyId: number
  isReadOnly?: boolean
}

const SubCompetencyAssessmentTitleCard = ({
  name,
  description,
  isFocus,
  evaluationId,
  subjectUserId,
  subCompetencyId,
  isReadOnly = false,
}: SubCompetencyAssessmentTitleCardProps) => {
  const dispatch = useDispatch()
  const [updateCompetencyEvaluation] = useUpdateCompetencyEvaluationMutation()

  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [confirmModalTitle, setConfirmModalTitle] = useState('')
  const [confirmModalDescription, setConfirmModalDescription] = useState('')
  const focusCompetenciesLimitReached = useSelector(
    (state: RootState) => state.growthWheel.focusCompetenciesLimit,
  )
  const assessmentSession = useSelector(
    (state: RootState) => state.growthWheel.assessmentSession,
  )
  const [confirmModalType, setConfirmModalType] = useState<ConfirmModalType>(
    isFocus ? ConfirmModalType.UNFOCUS : ConfirmModalType.FOCUS,
  )

  const handleToggleFocusClick = async () => {
    if (isReadOnly) return
    if (
      focusCompetenciesLimitReached &&
      confirmModalType === ConfirmModalType.FOCUS
    ) {
      setShowConfirmModal(true)
      setConfirmModalTitle('You have reached the limit.')
      setConfirmModalDescription(
        'You have reached the limit of 3 areas of focus. Please remove some areas of focus to add a new one.',
      )
      setConfirmModalType(ConfirmModalType.ALERT)
      return
    }
    const newFocus = isFocus
    setShowConfirmModal(true)
    setConfirmModalType(
      newFocus ? ConfirmModalType.UNFOCUS : ConfirmModalType.FOCUS,
    )
    setConfirmModalTitle(
      newFocus ? 'Remove Area of Focus?' : 'Mark as Area of Focus?',
    )
    setConfirmModalDescription(
      newFocus
        ? `Are you sure you want to remove "${name}" from your areas of focus?`
        : `Are you sure you want to mark "${name}" as an area of focus? This will help you and your Admin prioritize this sub-competency in your development.`,
    )
  }

  const handleCancelModal = () => {
    setShowConfirmModal(false)
    setTimeout(
      () =>
        setConfirmModalType(
          isFocus ? ConfirmModalType.UNFOCUS : ConfirmModalType.FOCUS,
        ),
      800,
    )
    setConfirmModalTitle('')
    setConfirmModalDescription('')
  }

  const handleConfirmFocus = async () => {
    if (confirmModalType === ConfirmModalType.ALERT) {
      setShowConfirmModal(false)
      return
    }
    await updateCompetencyEvaluation({
      evaluationId: evaluationId ?? 0,
      userId: subjectUserId,
      subCompetencyId,
      assessmentSessionId: assessmentSession?.id,
      payload: { focus: !isFocus },
    }).unwrap()
    dispatch(evaluateFocusCompetenciesLimit())
    dispatch(setCurrentAreasOfFocus())
    setShowConfirmModal(false)
  }

  useEffect(() => {
    setConfirmModalType(
      isFocus ? ConfirmModalType.UNFOCUS : ConfirmModalType.FOCUS,
    )
  }, [isFocus])

  return (
    <>
      <div className="card gw-competency-assessment-title-card">
        <div className="card-body gw-competency-area-view-body">
          <div className="d-flex align-items-center gap-2 ">
            <h3 className="gw-competency-area-view-title">{name}</h3>
            <span
              onClick={!isReadOnly ? handleToggleFocusClick : undefined}
              role="button"
              aria-disabled={isReadOnly}
              className={`p-1 rounded gw-competency-focus-button ${
                isReadOnly ? 'opacity-50' : ''
              } ${isFocus ? 'text-warning' : 'text-secondary'}`}
              title={
                isReadOnly
                  ? 'Locked assessments cannot be changed'
                  : isFocus
                  ? 'Remove from areas of focus'
                  : 'Mark as area of focus'
              }
            >
              <FontAwesomeIcon
                icon={isFocus ? faStarSolid : faStarRegular}
                style={{ fontSize: '1.3rem' }}
              />
            </span>
          </div>
          <RichTextView
            html={description}
            className="gw-competency-area-view-description"
          />
          {isFocus && (
            <div className="gw-competency-title-card-focus-badge">
              <FontAwesomeIcon icon={faStarSolid} className="w-4 h-4" />
              Marked as Area of Focus
            </div>
          )}
        </div>
      </div>
      <ConfirmationModal
        modalType={confirmModalType}
        icon={
          <FontAwesomeIcon
            icon={isFocus ? faStarSolid : faStarRegular}
            style={{ fontSize: '1.3rem' }}
          />
        }
        show={showConfirmModal}
        title={confirmModalTitle}
        description={confirmModalDescription}
        onConfirm={() => void handleConfirmFocus()}
        onCancel={() => handleCancelModal()}
      />
    </>
  )
}

export default React.memo(SubCompetencyAssessmentTitleCard)
